import { compile } from "path-to-regexp";
import queryString from "query-string";
import { EngineEarsError } from "../../store/actions/errorStore";
import { Release, ReleaseTrack, ReleaseType } from "../../store/models/release";
import { BareUser } from "../../store/models/user";
import {
  makeBackendGetCallWithJsonResponse,
  makeBackendPostCallWithJsonResponse,
  makeBackendPutCallWithJsonResponse,
} from "../../store/utils/fetch";
import {
  ARTIST_USER_RELEASE_ROUTE,
  CREATE_RELEASE_FROM_SCHEDULED_PROJECT,
  DOWNLOAD_RELEASE_TRACKS,
  FEATURED_RELEASE_ROUTE,
  RELEASE_CRUD_ROUTE,
  RELEASE_DETAILS_ROUTE,
  RELEASE_TRACK_ROUTE,
  RELEASE_TRACKS_ROUTE,
  SCHEDULED_PROJECT_RELEASE_ROUTE,
  TRENDING_ARTIST_RELEASE_TRACKS_ROUTE,
  UPDATE_TRACK_ORDER_ROUTE,
} from "../../store/utils/routes";
import { promiseEngineEarsHandler } from "../helpers";
import { Product } from "../products/models";
import {
  SupplyChainGenre,
  SupplyChainLanguage,
  SupplyChainParentalAdvisory,
  SupplyChainPrimaryGenre,
} from "../supplyChain/supplyChainModels";

export const fetchReleaseByReleaseId = async (releaseId: number) => {
  const route = `${RELEASE_CRUD_ROUTE}?release_id=${releaseId}`;
  return await makeBackendGetCallWithJsonResponse<Release | EngineEarsError>(
    route,
    "",
  );
};

export interface CreateReleaseArgs {
  release_type: ReleaseType;
  title: string;
  // If prices are set, then a product will also be created
  total_price?: number;
  unit_price?: number;
}

interface CreateReleaseResponse {
  release: Release;
  product?: Product;
}

/**
 * Creates a new release (not tied to a scheduled project).
 * If total_price and unit_price are set, then a (DTC) product will also be created.
 */
export const createRelease = async (args: CreateReleaseArgs) => {
  return await makeBackendPostCallWithJsonResponse<CreateReleaseResponse>(
    RELEASE_CRUD_ROUTE,
    args,
  ).then(promiseEngineEarsHandler);
};

export const fetchReleaseByScheduledProjectId = async (
  scheduledProjectId: number,
) => {
  const route = compile(SCHEDULED_PROJECT_RELEASE_ROUTE)({
    scheduled_project_id: scheduledProjectId,
  });
  return await makeBackendGetCallWithJsonResponse<Release | EngineEarsError>(
    route,
    "",
  );
};

export interface ArtistReleaseResponse {
  data: Release[];
  count: number;
  current_page: number;
  total_pages: number;
}

export const fetchArtistReleases = async (
  artistUserId: number,
  releaseType: ReleaseType,
  pageParam: number,
  isEditMode: boolean,
  isSubmitted: boolean = false,
) => {
  const params = queryString.stringify({
    page: pageParam,
    is_edit_mode: isEditMode,
    is_submitted: isSubmitted,
  });
  const route = compile(ARTIST_USER_RELEASE_ROUTE)({
    artist_user_id: artistUserId,
    release_type: releaseType,
  });
  return await makeBackendGetCallWithJsonResponse<
    ArtistReleaseResponse | EngineEarsError
  >(`${route}?${params}`, "");
};

export const fetchFeaturedRelease = async (artistUserId: number) => {
  const route = compile(FEATURED_RELEASE_ROUTE)({
    artist_user_id: artistUserId,
  });
  return await makeBackendGetCallWithJsonResponse<Release | EngineEarsError>(
    route,
    "",
  );
};

export interface ArtistTrendingReleaseTracksResponse {
  data: ReleaseTrack[];
  count: number;
  current_page: number;
  total_pages: number;
}

export const fetchTrendingArtistReleaseTracks = async (
  pageParam: number,
  artistUserId = 0,
) => {
  const params = queryString.stringify({ page: pageParam });
  const route = compile(TRENDING_ARTIST_RELEASE_TRACKS_ROUTE)({
    artist_user_id: artistUserId,
  });
  return await makeBackendGetCallWithJsonResponse<
    ArtistTrendingReleaseTracksResponse | EngineEarsError
  >(`${route}?${params}`, "");
};

export const fetchReleaseTracks = async (releaseId: number) => {
  const route = compile(RELEASE_TRACKS_ROUTE)({ release_id: releaseId });
  return await makeBackendGetCallWithJsonResponse<
    ReleaseTrack[] | EngineEarsError
  >(route, "");
};

export interface ReleaseDetails {
  recording_engineer_users: BareUser[];
  mixing_engineer_users: BareUser[];
  dolby_atmos_mixing_engineer_users: BareUser[];
  mastering_engineer_users: BareUser[];
  // Temporarily hide play count
  // total_play: number;
}

export const fetchReleaseDetails = async (releaseId = 0) => {
  const route = compile(RELEASE_DETAILS_ROUTE)({ release_id: releaseId });
  return await makeBackendGetCallWithJsonResponse<
    ReleaseDetails | EngineEarsError
  >(route, "");
};

export const updateReleaseTrackOrder = async (
  releaseId: number,
  updatedTrackOrder: number[],
) => {
  return await makeBackendPutCallWithJsonResponse<{
    tracks: ReleaseTrack[];
    error_occurred: boolean;
  }>(UPDATE_TRACK_ORDER_ROUTE, {
    release_id: releaseId,
    updated_track_order: updatedTrackOrder,
  }).then(promiseEngineEarsHandler);
};

export const createReleaseFromScheduledProject = async (
  releaseType: ReleaseType,
  scheduledProjectId = 0,
  published = true,
  featured = true,
) => {
  const args = {
    scheduled_project_id: scheduledProjectId,
    release_type: releaseType,
    published,
    featured,
  };
  return await makeBackendPostCallWithJsonResponse<Release>(
    CREATE_RELEASE_FROM_SCHEDULED_PROJECT,
    args,
  );
};

export interface UpdateReleaseArgs {
  title?: string;
  artist_display_name?: string;
  agreed_to_terms?: boolean;
  description?: string;
  featured?: boolean;
  published_at?: string;
  published_end?: string | null;
  release_type?: ReleaseType;
  supply_chain_language?: SupplyChainLanguage["id"];
  supply_chain_main_genre?: SupplyChainPrimaryGenre["id"];
  supply_chain_secondary_genre?: SupplyChainGenre["id"];
  parental_advisory?: SupplyChainParentalAdvisory["id"];
  payment_required_for_playback?: boolean;
  deleted_at?: boolean;
}

export const updateRelease = async (
  releaseId: number,
  args: UpdateReleaseArgs,
) => {
  const route = `${RELEASE_CRUD_ROUTE}?release_id=${releaseId}`;
  return await makeBackendPutCallWithJsonResponse<Release>(route, args);
};

export interface UpdateReleaseTrackArgs {
  title?: string;
  artist?: string;
  featured_artists?: string;
  supply_chain_language?: SupplyChainLanguage["id"];
  supply_chain_main_genre?: SupplyChainPrimaryGenre["id"];
  supply_chain_secondary_genre?: SupplyChainGenre["id"];
  parental_advisory?: SupplyChainParentalAdvisory["id"];
  deleted_at?: boolean;
}

export const updateReleaseTrack = async (
  releaseTrackId: number,
  args: UpdateReleaseTrackArgs,
) => {
  const route = compile(RELEASE_TRACK_ROUTE)({
    release_track_id: releaseTrackId,
  });
  return makeBackendPutCallWithJsonResponse<ReleaseTrack>(route, args).then(
    promiseEngineEarsHandler,
  );
};

export const fetchReleaseTrackById = async (releaseTrackId: number) => {
  const route = compile(RELEASE_TRACK_ROUTE)({
    release_track_id: releaseTrackId,
  });
  return makeBackendGetCallWithJsonResponse<ReleaseTrack>(route, "").then(
    promiseEngineEarsHandler,
  );
};

export const downloadReleaseTracks = async (
  releaseId: number,
  params: string,
) => {
  const route = compile(DOWNLOAD_RELEASE_TRACKS)({
    release_id: releaseId,
  });
  return makeBackendGetCallWithJsonResponse<{
    download_urls: string[];
  }>(route, params);
};
